import React, { useCallback } from 'react';
import { getScrollbarSize, joinClassNames } from '@tanium/coreui-utils';
import { TooltippedCheckbox } from '@tanium/react-checkbox';
import { Disclosure } from '@tanium/react-disclosure';
import styled, { css as cssEmotion, cx } from '@tanium/react-emotion-9';
import { SortArrowIcon } from '@tanium/react-graphics';
import { getSelectionStatus, SelectionProps } from '@tanium/react-use-selection';
import { css } from '@tanium/style-system';
import { ColumnSortProps, SortDirectionType } from '../../hooks/useColumnSort';
import {
  HeaderBorder,
  HeaderDisclosure,
  HeaderFill,
  HeaderText,
  SortIcon,
} from '../../themeDefinitions';
import { Alignment, alignmentToFlexAlignValue } from '../../types/Alignment';
import { Id } from '../../types/Id';
import {
  RibbonColumn,
  RibbonListProps,
  UseExpansionProps,
  UseExpansionReturnValue,
} from '../../types/StackedRibbonListComponentTypes';

export const HEADER_HEIGHT = 32;

const ASC = 'ASC';
const DESC = 'DESC';

const SCROLLBAR_SIZE = getScrollbarSize();

const HeaderWrapper = styled('div')(
  css({
    padding: 0,
    fontSize: 12,
    fontWeight: 'bold',
    cursor: 'pointer',
    color: HeaderText,
    backgroundColor: HeaderFill,
    borderBottom: 1,
    borderColor: HeaderBorder,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    height: HEADER_HEIGHT,
    display: 'flex',
    userSelect: 'none',
  }),
);

const withListOverflowStyle = cssEmotion({
  paddingRight: SCROLLBAR_SIZE,
});

const DisclosureWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'visibility',
})<{
  visibility: 'visible' | 'hidden';
}>(
  css({
    color: HeaderDisclosure,
    padding: 1,
    cursor: 'pointer',
  }),
  ({ visibility }) =>
    css({
      visibility,
    }),
);

const SelectAllWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'visibility',
})<{
  visibility: 'visible' | 'hidden';
}>(
  css({
    padding: '8px',
    cursor: 'default',
  }),
  ({ visibility }) =>
    css({
      visibility,
    }),
);

const headerFieldStyle = ({
  alignment,
  width,
  grow = 1,
}: {
  alignment: Alignment | undefined;
  width?: number;
  grow?: number;
}) =>
  cssEmotion(
    {
      padding: 8,
      flexBasis: 0,
      alignItems: 'center',
      display: 'flex',
      flexGrow: grow,
      minWidth: 120,
    },
    alignment && { textAlign: alignment, justifyContent: alignmentToFlexAlignValue(alignment) },
    width && {
      minWidth: width,
      maxWidth: width,
      flexBasis: width,
      flexGrow: 0,
    },
  );

const noClickStyle = cssEmotion({ cursor: 'default' });

const headerFieldGroupStyle = cssEmotion({
  display: 'flex',
  flexGrow: 1,
});

const noWrapStyle = cssEmotion({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
});

const Arrow = styled(SortArrowIcon)(
  css({
    color: SortIcon,
    minWidth: 2,
  }),
);

const sortStyle = {
  [ASC]: undefined,
  [DESC]: cssEmotion({ transform: 'rotate(180deg)' }),
};

export interface HeaderProps<TId extends Id, TItem>
  extends Pick<
      SelectionProps<TId>,
      | 'clearSelection'
      | 'isSelectable'
      | 'selectAll'
      | 'selection'
      | 'selectionMode'
      | 'enableSelectAll'
    >,
    Pick<UseExpansionReturnValue<TId>, 'clearExpansion' | 'expandAll'>,
    Pick<
      UseExpansionProps<TId>,
      'isExpandable' | 'expansion' | 'expansionMode' | 'hideExpansionDisclosure' | 'enableExpandAll'
    >,
    Pick<ColumnSortProps, 'getColumnSortDirection' | 'toggleSort'>,
    Pick<
      RibbonListProps<TId, TItem>,
      | 'isSelectionEnabled'
      | 'itemCount'
      | 'totalItemCount'
      | 'sort'
      | 'onSortChange'
      | 'selectionTooltipContent'
    > {
  columns: RibbonColumn<TItem>[];
  isListOverflowing: boolean;
}

const SortIndicator = ({ sortDirection }: { sortDirection: SortDirectionType | undefined }) =>
  sortDirection ? <Arrow height={16} width={16} className={sortStyle[sortDirection]} /> : null;

/**
 * A header to place above a RibbonList containing column headers and integrated list controls.
 */
const RibbonListHeaderInner = React.memo(
  <TId extends Id, TItem>(props: HeaderProps<TId, TItem>) => {
    const {
      columns,
      isSelectable,
      isSelectionEnabled,
      selectAll,
      selection,
      selectionMode,
      selectionTooltipContent,
      expansionMode,
      clearSelection,
      isExpandable,
      itemCount,
      totalItemCount,
      expansion,
      expandAll,
      clearExpansion,
      toggleSort,
      getColumnSortDirection,
      isListOverflowing,
      hideExpansionDisclosure,
      enableExpandAll,
      enableSelectAll,
    } = props;
    const isSelected = getSelectionStatus<TId>(selection, selectionMode, itemCount) === 'all';
    const isExpanded =
      expansion &&
      expansionMode &&
      getSelectionStatus<TId>(expansion, expansionMode, totalItemCount || itemCount) === 'all';

    const handleExpandClick = useCallback<React.MouseEventHandler<HTMLDivElement>>(
      (e) => {
        e.stopPropagation();
        if (isExpanded) {
          clearExpansion();
        } else {
          expandAll();
        }
      },
      [clearExpansion, expandAll, isExpanded],
    );

    const handleSelectChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
      (e) => {
        if (e.target.checked) {
          selectAll();
        } else {
          clearSelection();
        }
      },
      [clearSelection, selectAll],
    );

    return (
      <div role="rowgroup">
        <HeaderWrapper role="row" className={cx(isListOverflowing && withListOverflowStyle)}>
          {isSelectable && (
            <SelectAllWrapper
              role="columnheader"
              visibility={enableSelectAll === false ? 'hidden' : 'visible'}
            >
              <TooltippedCheckbox
                checked={isSelected}
                data-select-all-checkbox
                disabled={isSelectionEnabled === false}
                onChange={handleSelectChange}
                tooltipContent={selectionTooltipContent}
                tooltipPlacement="right"
              />
            </SelectAllWrapper>
          )}
          {isExpandable && !hideExpansionDisclosure && (
            <DisclosureWrapper
              role="columnheader"
              data-expand-all
              // The disclosure is rendered invisibly when enableExpandAll is false,
              // to ensure column headers don't get out of alignment with the expandable
              // rows below.
              visibility={enableExpandAll === false ? 'hidden' : 'visible'}
              onClick={handleExpandClick}
            >
              <Disclosure aria-expanded={isExpanded} isCollapsed={!isExpanded} role="button" />
            </DisclosureWrapper>
          )}
          <div role="presentation" className={headerFieldGroupStyle}>
            {columns.map((c) => {
              const { alignment, center, dataKey, isSortDisabled, label, noWrap, width, grow } = c;
              return (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- TODO: need to add key event handler
                <div
                  role="columnheader"
                  tabIndex={0}
                  className={joinClassNames(
                    headerFieldStyle({
                      alignment: alignment ?? (center ? 'center' : undefined),
                      width,
                      grow,
                    }),
                    isSortDisabled && noClickStyle,
                    noWrap && noWrapStyle,
                  )}
                  key={dataKey}
                  // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- function depends on parameter passed to inner function
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isSortDisabled) {
                      toggleSort(dataKey);
                    }
                  }}
                >
                  <span className={noWrapStyle}>{label}</span>
                  {!isSortDisabled && (
                    <SortIndicator sortDirection={getColumnSortDirection(dataKey)} />
                  )}
                </div>
              );
            })}
          </div>
        </HeaderWrapper>
      </div>
    );
  },
);

export const RibbonListHeader = RibbonListHeaderInner as <TId extends Id, TItem>(
  props: HeaderProps<TId, TItem>,
) => JSX.Element;
